import "./index.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { loading_02 } from "@tql/ui-components-react/untitled/icons";
import { useAtomValue } from "jotai";
import { useCallback, useState } from "react";
import { useParams } from "react-router";

import { ShipmentNotificationModal } from "~/features/notifications/components/shipment-notification-modal/shipment-notification-modal.tsx";
import { Construction } from "~/shared/components/construction/construction.tsx";
import { DocumentViewer } from "~/shared/components/document-viewer/document-viewer.tsx";
import { useDocumentViewer } from "~/shared/components/document-viewer-flyout/hooks/use-document-viewer.ts";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import { ErrorModal } from "./components/error-modal/index.tsx";
import { GenerateLtlDocumentsModal } from "./components/generate-ltl-documents-modal/index.tsx";
import { useGetShipment } from "./hooks/use-get-shipment.ts";
import {
	useShipmentLock,
	useShipmentLockState,
} from "./hooks/use-shipment-lock.ts";
import { ExternalShipmentManagement } from "./layout/external-shipment-management.tsx";
import { InternalShipmentManagement } from "./layout/internal-shipment-management.tsx";
import { isEditingShipmentAtom } from "./state/shipment-edit-atoms.ts";
import type { AugmentedShipment } from "./types/augmented-shipment";

export const ShipmentManagement = () => {
	const { isReady, isEnabled } = useFeatureFlagGate(
		"traxLtlShipmentManagement",
	);
	const { isInternal } = useDeploymentType();
	const params = useParams();
	const { employeeId } = useIsAuthenticated();
	const isEditing = useAtomValue(isEditingShipmentAtom);

	const { viewerState, openViewer, closeViewer, selectDocument } =
		useDocumentViewer();

	const {
		shipment,
		isLoading,
		error: shipmentError,
	} = useGetShipment(Number(params["poNumber"]), isInternal);

	useShipmentLock({
		poNumber: params["poNumber"] ?? "",
		enabled: isInternal && isReady && isEnabled,
		shouldHoldLock: isEditing,
		isShipmentLoaded: !!shipment,
		lock: shipment?.loadAccess?.lock ?? null,
		currentUserId: employeeId,
	});

	const { isResolved: isLockResolved } = useShipmentLockState();

	const handleDocumentClick = useCallback(() => {
		openViewer();
	}, [openViewer]);

	const [notificationModalOpen, setNotificationModalOpen] = useState(false);

	const handleCloseNotifications = useCallback(() => {
		setNotificationModalOpen(false);
	}, []);

	const [generateDocumentsModalOpen, setGenerateDocumentsModalOpen] =
		useState(false);

	if (!isReady || isLoading) {
		return <LoadingSpinner />;
	}

	if (!isEnabled) {
		return <Construction />;
	}

	// Internal users must resolve the edit lock (get shipment + lock call) before
	// the page is shown — until then, only the spinner is visible.
	if (isInternal && !shipmentError && !isLockResolved) {
		return <LoadingSpinner />;
	}

	if (!shipment) {
		return shipmentError ? (
			<ErrorModal error={shipmentError} show={true} />
		) : (
			<LoadingSpinner />
		);
	}

	const isLtl = shipment.shipmentDetails?.additionalDetails?.rateType === "LTL";
	const customerId = shipment.customer?.id ?? 0;
	const poNumber = Number(params["poNumber"]);
	// Only shown while editing an internal LTL shipment — external users and
	// non-edit views never render this action.
	const canGenerateLtlDocuments = isInternal && isLtl;

	return (
		<>
			<DocumentViewer
				refetchOnOpen
				extraHeaderActions={
					canGenerateLtlDocuments ? (
						<Button
							id={"borderless-button"}
							onClick={() => {
								setGenerateDocumentsModalOpen(true);
							}}
							startIcon={<SvgIcon icon={loading_02} />}
						>
							Generate LTL BOL and Labels
						</Button>
					) : undefined
				}
				onClose={closeViewer}
				onSelectDocument={selectDocument}
				poNumber={poNumber}
				viewerState={viewerState}
			/>
			{canGenerateLtlDocuments ? (
				<GenerateLtlDocumentsModal
					onToggleModal={() => {
						setGenerateDocumentsModalOpen(false);
					}}
					poNumber={poNumber}
					shipment={shipment as AugmentedShipment}
					showModal={generateDocumentsModalOpen}
				/>
			) : null}
			{isInternal ? (
				<InternalShipmentManagement
					// TODO: Re-enable shipment locking in a future release.
					// disabled={isLockedByOther}
					error={shipmentError}
					handleDocumentClick={handleDocumentClick}
					shipment={shipment as AugmentedShipment}
				/>
			) : (
				<ExternalShipmentManagement
					error={shipmentError}
					handleDocumentClick={handleDocumentClick}
					shipment={shipment as AugmentedShipment}
				/>
			)}
			{isLtl ? (
				<ShipmentNotificationModal
					customerId={customerId}
					isOpen={notificationModalOpen}
					onClose={handleCloseNotifications}
					poNumber={poNumber}
				/>
			) : null}
		</>
	);
};
